import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Menu, MenuItem, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useQueryClient } from '@tanstack/react-query';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useTaskStatus } from 'api/connectors';
import { extractApiError } from 'api/errors';
import { useTriggerDbtProject, warehouseKeys } from 'api/warehouse';
import useRole from 'hooks/useRole';
import { useTokens } from 'theme';

const COMMANDS = ['dbt build', 'dbt run', 'dbt test', 'dbt snapshot', 'dbt seed'];

/** One dispatch state for the default action and every command in its menu. */
export default function PreparationRunButton({ projectId, disabled = false }) {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const tokens = useTokens();
  const client = useQueryClient();
  const trigger = useTriggerDbtProject(projectId);
  const [anchor, setAnchor] = useState(null);
  const [starting, setStarting] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [reason, setReason] = useState(null);
  const [pendingCommand, setPendingCommand] = useState(null);
  const menuId = useId();
  const menuButtonId = useId();
  const confirmationTitleId = useId();
  const confirmationDescriptionId = useId();
  const primaryButton = useRef(null);
  const menuButton = useRef(null);
  const confirmationOrigin = useRef(null);
  const restoreAfterDialog = useRef(false);
  const mounted = useRef(true);
  const dispatching = useRef(false);
  const outcomeTimer = useRef(null);
  const task = useTaskStatus(taskId);
  const busy = starting || Boolean(taskId);
  const blocked = disabled || !isAdmin || busy || Boolean(outcome);
  const controlsBlocked = blocked || Boolean(pendingCommand);

  const refresh = useCallback(() => {
    [warehouseKeys.project(projectId), warehouseKeys.projects, warehouseKeys.readiness(projectId), warehouseKeys.runs(projectId)]
      .forEach((queryKey) => { client.invalidateQueries({ queryKey, exact: true }); });
  }, [client, projectId]);

  const finish = useCallback((result, message) => {
    dispatching.current = false;
    setTaskId(null);
    setOutcome(result);
    setReason(message || null);
    clearTimeout(outcomeTimer.current);
    outcomeTimer.current = setTimeout(() => {
      setOutcome(null);
      setReason(null);
    }, result === 'started' ? 4000 : 6000);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(outcomeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (blocked) {
      setAnchor(null);
      setPendingCommand(null);
      restoreAfterDialog.current = false;
    }
  }, [blocked]);

  useEffect(() => {
    setAnchor(null);
    setPendingCommand(null);
    restoreAfterDialog.current = false;
  }, [projectId]);

  useEffect(() => {
    if (!taskId) return;
    if (task.error) {
      finish('error', t('data.workspacePreparation.launchStatusUnavailable', { defaultValue: 'Could not confirm the launch. Check run history.' }));
      refresh();
    } else if (task.data?.status === 'REVOKED') {
      finish('cancelled', t('data.workspacePreparation.launchCancelled', { defaultValue: 'The preparation launch was cancelled.' }));
      refresh();
    } else if (task.data?.status === 'FAILURE') {
      // Task output can contain infrastructure details; keep it out of the UI.
      finish('error', t('data.workspacePreparation.startFailed', { defaultValue: 'Could not start preparation. Check run history.' }));
      refresh();
    } else if (task.data?.status === 'SUCCESS') {
      // This task dispatches Prefect. Its success does not mean dbt completed.
      if (task.data.result?.status === 'dispatched') finish('started');
      else if (task.data.result?.status === 'superseded') finish('error', t('common.dbtRunInProgress'));
      else finish('error', t('data.workspacePreparation.startFailed', { defaultValue: 'Could not start preparation. Check run history.' }));
      refresh();
    }
  }, [taskId, task.data, task.error, finish, refresh, t]);

  const start = async (command) => {
    if (blocked || dispatching.current || !COMMANDS.includes(command)) return;
    dispatching.current = true;
    setAnchor(null);
    setPendingCommand(null);
    setStarting(true);
    try {
      const result = await trigger.mutateAsync(command);
      if (!mounted.current) return;
      refresh();
      if (result?.task_id) setTaskId(result.task_id);
      else finish('error', t('data.workspacePreparation.launchStatusUnavailable', { defaultValue: 'Could not confirm the launch. Check run history.' }));
    } catch (error) {
      if (mounted.current) finish('error', extractApiError(error, t('data.workspacePreparation.startFailed', { defaultValue: 'Could not start preparation. Check run history.' })));
    } finally {
      if (mounted.current) setStarting(false);
    }
  };

  const requestStart = (command, origin) => {
    if (controlsBlocked || dispatching.current || !COMMANDS.includes(command)) return;
    setAnchor(null);
    if (command === 'dbt test') {
      start(command);
      return;
    }
    confirmationOrigin.current = origin === 'menu' ? menuButton.current : primaryButton.current;
    restoreAfterDialog.current = false;
    setPendingCommand({ command, projectId });
  };
  const cancelConfirmation = () => {
    restoreAfterDialog.current = true;
    setPendingCommand(null);
  };
  const confirm = () => {
    const pending = pendingCommand;
    restoreAfterDialog.current = false;
    setPendingCommand(null);
    if (!pending || pending.projectId !== projectId || blocked || dispatching.current) return;
    start(pending.command);
  };
  const restoreConfirmationFocus = () => {
    const target = confirmationOrigin.current;
    if (restoreAfterDialog.current && target?.isConnected && !target.disabled) target.focus();
    restoreAfterDialog.current = false;
  };

  if (!isAdmin) return null;
  const menuOpen = Boolean(anchor) && !controlsBlocked;
  const confirmationOpen = Boolean(pendingCommand) && pendingCommand.projectId === projectId && !blocked;
  const label = busy ? t('data.workspacePreparation.running', { defaultValue: 'Starting preparation…' })
    : outcome === 'started' ? t('data.workspacePreparation.started', { defaultValue: 'Launch accepted' })
      : outcome === 'cancelled' ? t('shell.cancelled')
        : outcome === 'error' ? t('shell.failed') : t('data.workspacePreparation.runNow', { defaultValue: 'Run now' });
  const icon = busy ? <CircularProgress size={14} color="inherit" />
    : outcome === 'started' ? <CheckIcon /> : outcome ? <ErrorOutlineIcon /> : <PlayArrowIcon />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: 232, maxWidth: '100%', minWidth: 0, ml: 'auto', flexShrink: 0 }}>
      <ButtonGroup variant="contained" size="small" color={outcome === 'error' ? 'error' : outcome === 'cancelled' ? 'warning' : outcome === 'started' ? 'success' : 'primary'} aria-label={t('data.workspacePreparation.runNow', { defaultValue: 'Run now' })} sx={{
        width: '100%', minWidth: 0,
        '& .MuiButtonGroup-grouped:first-of-type': { flex: '1 1 0%', minWidth: 0, borderColor: alpha(tokens.onAccent, 0.2), whiteSpace: 'nowrap', px: 1 },
        '& .MuiButtonGroup-grouped:last-of-type': { flex: '0 0 36px', width: 36, minWidth: 36, px: 0.75 },
        '@media (pointer: coarse)': { '& .MuiButtonGroup-grouped': { minHeight: 44 }, '& .MuiButtonGroup-grouped:last-of-type': { flexBasis: 44, width: 44, minWidth: 44 } },
      }}>
        <Button ref={primaryButton} startIcon={icon} disabled={controlsBlocked} onClick={() => requestStart('dbt build', 'primary')}>{label}</Button>
        <Button
          ref={menuButton}
          id={menuButtonId}
          aria-label={t('data.workspacePreparation.commandMenu', { defaultValue: 'Choose preparation command' })}
          aria-haspopup="menu"
          aria-controls={menuOpen ? menuId : undefined}
          aria-expanded={menuOpen ? 'true' : undefined}
          disabled={controlsBlocked}
          onClick={(event) => setAnchor(event.currentTarget)}
        ><ArrowDropDownIcon /></Button>
      </ButtonGroup>
      <Menu id={menuId} anchorEl={anchor} open={menuOpen} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} MenuListProps={{ 'aria-labelledby': menuButtonId }}>
        {COMMANDS.map((command) => <MenuItem key={command} disabled={controlsBlocked} onClick={() => requestStart(command, 'menu')}>{command}</MenuItem>)}
      </Menu>
      <Dialog open={confirmationOpen} onClose={cancelConfirmation} maxWidth="sm" fullWidth disableRestoreFocus aria-labelledby={confirmationTitleId} aria-describedby={confirmationDescriptionId} TransitionProps={{ onExited: restoreConfirmationFocus }}>
        <DialogTitle id={confirmationTitleId}>{t('data.workspacePreparation.confirmRunTitle', { defaultValue: 'Run project transformation?' })}</DialogTitle>
        <DialogContent id={confirmationDescriptionId}>
          <DialogContentText>{t('data.workspacePreparation.confirmRunScope', { defaultValue: 'Command {{command}} will run for the entire project.', command: pendingCommand?.command || '' })}</DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>{t('data.workspacePreparation.confirmRunNodeHint', { defaultValue: 'To rebuild a single node, click it in the graph and choose “Reset”.' })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelConfirmation} color="inherit">{t('data.cancel')}</Button>
          <Button onClick={confirm} variant="contained" disabled={blocked || !pendingCommand}>{t('data.workspacePreparation.confirmRunAction', { defaultValue: 'OK' })}</Button>
        </DialogActions>
      </Dialog>
      {['error', 'cancelled'].includes(outcome) && <Typography role="alert" variant="caption" color={outcome === 'error' ? 'error' : 'text.secondary'} sx={{ mt: 0.75, width: '100%', minWidth: 0, overflowWrap: 'anywhere' }}>{reason}</Typography>}
    </Box>
  );
}
